import PropTypes from 'prop-types';
import { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { getAppConfig, message as messageApi } from '@icp/settings';
import { parseJSON, isMobile } from '@icp/utils';
import { AIAgentFloatButton, AIAgentV2, Loading } from '@icp/components';
import { translateEntireObj } from '@icp/i18n';
import markdownCodeComponents from './markdownCodeComponents';
import { handleSave } from './markdownCodeComponents/utils';
import { useCurrentPageForm } from './CurrentPageFormStoreProvider';
import { loadFormEntity } from './markdownCodeComponents/formEntityLoader';

const ACTION_PROMPTS = {
  OPEN_FORM: '// action:open_form',
};

const GeneralAIAgent = forwardRef(({ suppressFloatButton, eventHandlers, ...otherProps }, ref) => {
  const { t } = useTranslation();
  const location = useLocation();

  const buttonRef = useRef();
  const aiAgentRef = useRef();
  const aiAgentDelegates = useRef({});
  const currentPageForm = useCurrentPageForm();

  useEffect(() => {
    if (!currentPageForm) return () => {};

    const listener = () => {
      const {
        context: { pbcToken, formEntityToken },
        formData: { values: formEntityData },
      } = currentPageForm.store.getState();

      const newUserContext =
        pbcToken && formEntityToken
          ? {
              pbcToken,
              formEntityToken,
              formEntityData,
            }
          : null;
      aiAgentDelegates.current.getUserContext = () => newUserContext;
      sendUserContextMessage();
    };

    currentPageForm.formApi.on('ready', listener);
    if (currentPageForm.formApi.stage === 'ready') {
      listener();
    }
    return () => {
      currentPageForm.formApi.off('ready', listener);
    };
  }, [currentPageForm]);

  const onCustomSSEMessage = (message) => {
    if (/^FORM_AUTOFILL$/i.test(message.event)) {
      const data = parseJSON(parseJSON(parseJSON(message.data)?.data)?.form_data);
      if (data) {
        currentPageForm?.formApi?.setData(data);
      }
    }
  };

  const promptAnswerActions = (msgContent) =>
    [
      /```(icp-table|icp-chart|icp-generic-chart)/.test(msgContent || '') && {
        label: t('save', { ns: 'icp-common' }),
        handlerKey: 'onSave',
      },
    ].filter(Boolean);

  const sendUserContextMessage = () => {
    if (!buttonRef.current?.open) return;
    const userContext = aiAgentDelegates.current?.getUserContext?.();
    if (!userContext?.pbcToken || !userContext?.formEntityToken) return;
    const message = `${ACTION_PROMPTS.OPEN_FORM}\n${JSON.stringify(userContext)}`;
    aiAgentRef.current?.sendMessage(message, [], userContext);
  };

  useImperativeHandle(ref, () => ({
    get sendMessage() {
      return aiAgentRef.current?.sendMessage;
    },
  }));

  if (location.pathname.startsWith('/mobile/system-ai-agent')) return null;
  if (getAppConfig()?.agent?.showFloatButton === 'hide') return null;

  const agentElement = (
    <AIAgentV2
      ref={aiAgentRef}
      markdownCodeComponents={markdownCodeComponents}
      eventHandlers={{
        onSave: (msg, question) =>
          handleSave(msg, currentPageForm?.formApi, question).then(() => {
            messageApi.success(t('chat.save-portlet-success', { ns: 'default' }));
          }),
        onCustomSSEMessage,
        renderPromptText,
        ...eventHandlers,
      }}
      promptAnswerActions={promptAnswerActions}
      {...otherProps}
    />
  );

  if (suppressFloatButton) return agentElement;

  const defaultOpen = isMobile()
    ? window.__bot_conf__?.mobileSystemAgentDefaultOpen ??
      window.__bot_conf__?.systemAgentDefaultOpen
    : window.__bot_conf__?.systemAgentDefaultOpen;

  return (
    <AIAgentFloatButton ref={buttonRef} defaultOpen={defaultOpen}>
      {agentElement}
    </AIAgentFloatButton>
  );
});

GeneralAIAgent.propTypes = {
  suppressFloatButton: PropTypes.bool,
  eventHandlers: PropTypes.shape({}),
};

export default GeneralAIAgent;

function FormEntityName(props) {
  // eslint-disable-next-line react/prop-types
  const { pbcToken, formEntityToken } = props;
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formEntity, setFormEntity] = useState(null);

  useEffect(() => {
    if (!pbcToken || !formEntityToken) return;
    setLoading(true);
    loadFormEntity(pbcToken, formEntityToken)
      .then((data) => {
        translateEntireObj(data);
        setFormEntity(data);
      })
      .finally(() => setLoading(false));
  }, [pbcToken, formEntityToken]);

  if (loading) return <Loading />;

  const formName = formEntity?.name || formEntityToken;
  return t('ai.event.open-form', { form: formName });
}

export const renderPromptText = (text) => {
  if (text.startsWith(ACTION_PROMPTS.OPEN_FORM)) {
    const userContext = JSON.parse(text.replace(ACTION_PROMPTS.OPEN_FORM, ''));
    return <FormEntityName {...userContext} />;
  }
  return text;
};
